import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers, banUser, unbanUser, deleteUser, resetUserPassword } from "../../api/admin";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogDescription,
} from "../../components/ui/dialog";
import Loader from "../../components/ui/Loader";
import {
 Trash2,
 Ban,
 CheckCircle,
 ShieldCheck,
 XCircle,
 Users,
 Search,
 ArrowLeft,
 KeyRound,
 Loader2,
} from "lucide-react";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { toast } from "react-hot-toast";

const ManageUsers = () => {
 const navigate = useNavigate();
 const [users, setUsers] = useState([]);
 const [roleFilter, setRoleFilter] = useState("");
 const [searchTerm, setSearchTerm] = useState("");
 const [loading, setLoading] = useState(true);
 const [resetUser, setResetUser] = useState(null);
 const [newPassword, setNewPassword] = useState("");
 const [resetting, setResetting] = useState(false);

 const fetchUsers = async (role = "", search = "") => {
 setLoading(true);
 try {
 const res = await getUsers(role, search);
 setUsers(res.data);
 } catch (err) {
 console.error("Error fetching users:", err);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchUsers(roleFilter, searchTerm);
 }, [roleFilter, searchTerm]);

 const handleBan = async (id) => {
 const res = await banUser(id);
 setUsers((prev) => prev.map((u) => (u.id === id ? res.data : u)));
 };

 const handleUnban = async (id) => {
 const res = await unbanUser(id);
 setUsers((prev) => prev.map((u) => (u.id === id ? res.data : u)));
 };

 const handleDelete = async (id) => {
 await deleteUser(id);
 setUsers((prev) => prev.filter((u) => u.id !== id));
 };

 const handleResetPassword = async () => {
 if (!resetUser || newPassword.length < 6) {
 toast.error("Password must be at least 6 characters");
 return;
 }
 setResetting(true);
 try {
 await resetUserPassword(resetUser.id, newPassword);
 toast.success("Password reset successfully");
 setResetUser(null);
 setNewPassword("");
 } catch (err) {
 toast.error(err.response?.data?.message || "Failed to reset password");
 } finally {
 setResetting(false);
 }
 };

 if (loading) return <Loader />;

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg">
 <Users className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">User Management</h1>
 </div>
 <p className="text-muted-foreground">Control access, verification and user roles</p>
 </div>

 {/* Filters */}
 <Card className="mb-6">
 <CardContent className="p-6">
 <div className="flex flex-col md:flex-row gap-4">
 {/* Search Box */}
 <div className="relative flex-1">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 type="text"
 placeholder="Search by name, email, company..."
 className="pl-10"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 />
 </div>

 {/* Role Filters */}
 <div className="flex flex-wrap gap-2">
 {["", "ADMIN", "RECRUITER", "APPLIER", "BANNED"].map((role) => (
 <Button
 key={role}
 size="sm"
 variant={roleFilter === role ? "default" : "outline"}
 onClick={() => setRoleFilter(role)}
 >
 {role === "" ? "All Users" : role}
 </Button>
 ))}
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Users List */}
 {users.length === 0 ? (
 <Card>
 <CardContent className="text-center py-16">
 <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
 <p className="text-lg text-muted-foreground">No users found</p>
 </CardContent>
 </Card>
 ) : (
 <div className="space-y-4">
 {users.map((user) => (
 <Card key={user.id} className="hover:shadow-md transition-shadow">
 <CardContent className="p-5">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 {/* User Info */}
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 <h3 className="font-semibold text-lg truncate">
 {user.applierName || user.recruiterCompanyName || "No Name"}
 </h3>
 <Badge variant={
 user.role === "ADMIN" ? "default" :
 user.role === "RECRUITER" ? "secondary" :
 user.role === "BANNED" ? "destructive" :
 "outline"
 }>
 {user.role}
 </Badge>
 </div>
 <p className="text-sm text-muted-foreground mb-2">{user.email}</p>
 <div>
 {user.isVerified ? (
 <Badge variant="outline" className="bg-green-500/10 border-green-200 text-green-700 dark:border-green-800 dark:text-green-300">
 <ShieldCheck className="w-3 h-3 mr-1" />
 Verified
 </Badge>
 ) : (
 <Badge variant="outline" className="bg-amber-500/10 border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300">
 <XCircle className="w-3 h-3 mr-1" />
 Not Verified
 </Badge>
 )}
 </div>
 </div>

 {/* Actions */}
 <div className="flex flex-wrap gap-2">
 <Button
 size="sm"
 variant="outline"
 onClick={() => {
 setResetUser(user);
 setNewPassword("");
 }}
 >
 <KeyRound className="w-4 h-4 mr-2" />
 Reset Password
 </Button>

 <ConfirmDialog
 trigger={
 <Button size="sm" variant="outline" className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground">
 <Trash2 className="w-4 h-4 mr-2" />
 Delete
 </Button>
 }
 title="Delete User"
 description="Are you sure you want to delete this user permanently? This action cannot be undone."
 onConfirm={() => handleDelete(user.id)}
 confirmText="Delete"
 variant="destructive"
 />

 {user.role !== "BANNED" ? (
 <ConfirmDialog
 trigger={
 <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-500/10 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-900/20">
 <Ban className="w-4 h-4 mr-2" />
 Ban
 </Button>
 }
 title="Ban User"
 description="Are you sure you want to ban this user? They will no longer be able to log in."
 onConfirm={() => handleBan(user.id)}
 confirmText="Ban"
 variant="destructive"
 />
 ) : (
 <ConfirmDialog
 trigger={
 <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-500/10 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/20">
 <CheckCircle className="w-4 h-4 mr-2" />
 Unban
 </Button>
 }
 title="Unban User"
 description="Are you sure you want to unban this user? They will be able to log in again."
 onConfirm={() => handleUnban(user.id)}
 confirmText="Unban"
 />
 )}
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 <Dialog open={!!resetUser} onOpenChange={(open) => !open && setResetUser(null)}>
 <DialogContent className="sm:max-w-md">
 <DialogHeader>
 <DialogTitle>Reset Password</DialogTitle>
 <DialogDescription>
 Set a new password for {resetUser?.applierName || resetUser?.recruiterCompanyName || resetUser?.email}
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 pt-2">
 <div className="space-y-2">
 <Label htmlFor="resetPassword">New Password</Label>
 <Input
 id="resetPassword"
 type="password"
 placeholder="At least 6 characters"
 value={newPassword}
 onChange={(e) => setNewPassword(e.target.value)}
 />
 </div>
 <div className="flex justify-end gap-2">
 <Button variant="outline" onClick={() => setResetUser(null)}>
 Cancel
 </Button>
 <Button onClick={handleResetPassword} disabled={resetting || newPassword.length < 6}>
 {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
 Reset Password
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </div>
 );
};

export default ManageUsers;
